import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const PROMISE = "One real business story every morning, and what it means if you run a product business in India.";

export function NewsletterInline({ source = "unknown", dark = false, compact = false, promise = PROMISE, doneText, finePrint = "Free. One email a day. Unsubscribe in one click." }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [state, setState] = useState("idle"); // idle | done | busy
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("busy");
    try { await api.post("/subscribe", { email, first_name: name, source }); setState("done"); }
    catch { setState("done"); }
  };
  if (state === "done") {
    return (
      <div data-testid={`newsletter-done-${source}`} className={`rounded-xl border p-5 ${dark ? "border-white/15 bg-white/5 text-[#E8E4DA]" : "border-border bg-card text-ink"}`}>
        <p className="font-heading text-lg font-semibold">Check your inbox.</p>
        <p className={`mt-1 text-sm ${dark ? "text-[#9E9A90]" : "text-muted"}`}>{doneText || "We've sent a one-click confirmation link. Confirm it and the first story lands tomorrow morning."}</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} data-testid={`newsletter-form-${source}`} className={compact ? "" : `rounded-xl border p-6 ${dark ? "border-white/15 bg-white/[0.03]" : "border-border bg-card"}`}>
      {!compact && <p className={`text-[15px] leading-relaxed ${dark ? "text-[#D6D2C8]" : "text-ink"}`}>{promise}</p>}
      <div className={`mt-4 flex flex-col gap-2 sm:flex-row ${compact ? "mt-0" : ""}`}>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="First name" className="h-11 sm:max-w-[160px]" data-testid={`newsletter-name-${source}`} />
        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.in" className="h-11 w-full sm:flex-1" data-testid={`newsletter-email-${source}`} />
        <Button type="submit" disabled={state === "busy"} className="h-11 rounded-lg bg-accent px-6 font-semibold text-white hover:opacity-90" data-testid={`newsletter-submit-${source}`}>
          {state === "busy" ? "…" : "Subscribe"}
        </Button>
      </div>
      {!compact && <p className={`mt-2 text-xs ${dark ? "text-[#6B6862]" : "text-muted"}`}>{finePrint}</p>}
    </form>
  );
}

export function ExitIntentModal() {
  const [open, setOpen] = useState(false);
  const fired = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia && window.matchMedia("(max-width: 768px)").matches) return; // desktop only
    if (localStorage.getItem("desk_exit_seen")) return;
    const onLeave = (e) => {
      if (fired.current) return;
      if (e.clientY <= 0) {
        fired.current = true;
        localStorage.setItem("desk_exit_seen", "1");
        setOpen(true);
      }
    };
    document.addEventListener("mouseleave", onLeave);
    return () => document.removeEventListener("mouseleave", onLeave);
  }, []);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" data-testid="exit-intent-modal" onClick={() => setOpen(false)}>
      <div className="relative w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => setOpen(false)} className="absolute right-4 top-4 text-slate-400 hover:text-navy" data-testid="exit-intent-close"><X className="h-5 w-5" /></button>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald">Before you go</p>
        <h3 className="mt-3 font-heading text-2xl font-bold text-navy">{PROMISE}</h3>
        <div className="mt-5"><NewsletterInline source="exit-intent" compact /></div>
      </div>
    </div>
  );
}
